<script setup lang="ts">
import { computed, inject, ref, watch, type PropType, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import { useQuery, useMutation, keepPreviousData } from '@tanstack/vue-query';
import * as yup from 'yup';
import { caregiversApi, type CaregiverFilter } from '@/api/caregivers';
import { patientsApi, type PatientFilter } from '@/api/patients';
import { shiftsApi, type ShiftRecurrentCreateForm } from '@/api/shifts';
import type { AxiosError } from 'axios';
import { type SerializedUser } from '@/api/users';
import PriceInput from '@/components/price-input.vue';
import TimeInput from '@/components/time-input.vue';
import ComplianceCheckModal from '@/components/shifts/compliance-check-modal.vue';
import ShiftConflictDetector from '@/components/shifts/shift-conflict-detector.vue';
import { usePricingStructure } from '@/composables/usePricingStructure';
import { usePermissions } from '@/composables/usePermissions';
import { useAnalytics, AnalyticsEvents } from '@/composables/useAnalytics';
import { useGroupFilterStore } from '@/stores/group-filter';
import ShiftsInfoBanner from '@/components/shifts/shifts-info-banner.vue';
import { requestErrorMessage } from '@/utils/api-errors';
import { memberWithoutGroups, resolveMemberGroupIds } from '@/utils/group-scope';
import { MINUTES_PER_HOUR, addMinutesToTime, endTimeFollowingStart } from '@/utils/time-of-day';

const { t } = useI18n();

const emit = defineEmits(['close', 'refetch', 'success']);

const currentUser = inject<Ref<SerializedUser>>('currentUser');

const { hasPermission } = usePermissions();
const { trackShiftEvent } = useAnalytics();

const props = defineProps({
  patient: {
    type: Object as PropType<PatientFilter> || undefined,
    default: undefined,
  },
  caregiver: {
    type: Object as PropType<CaregiverFilter> || undefined,
    default: undefined,
  },
});

const financialNumberField = hasPermission.value('shifts', 'read_financial_information') ?
  yup.number().required(t('shiftRecurrentCreateForm.fieldRequired'))
    .typeError(t('shiftRecurrentCreateForm.fieldRequired')) : yup.number();

const commonValidationSchema = {
  patientId: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  caregiverId: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  withTaxBill: hasPermission.value('shifts', 'read_financial_information') ?
    yup.boolean().required(t('shiftRecurrentCreateForm.fieldRequired')) : yup.boolean(),
  startDate: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  endDate: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  shiftType: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  startTime: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  endTime: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  recurrenceType: yup.string().required(t('shiftRecurrentCreateForm.fieldRequired')),
  recurrenceInterval: yup.number()
    .min(1, t('shiftRecurrentCreateForm.fieldPositive'))
    .required(t('shiftRecurrentCreateForm.fieldRequired')),
  recurrenceDays: yup.array().of(yup.string()).when('recurrenceType', {
    is: 'weekly',
    then: (schema) => schema.required(t('shiftRecurrentCreateForm.fieldRequired')),
    otherwise: (schema) => schema.notRequired(),
  }),
};

const normalValidationSchema = yup.object({
  ...commonValidationSchema,
  caregiverWeekShiftCost: financialNumberField,
  caregiverWeekendShiftCost: financialNumberField,
  weekShiftPrice: financialNumberField,
  weekendShiftPrice: financialNumberField,
});

const perHourValidationSchema = yup.object({
  ...commonValidationSchema,
  caregiverWeekShiftCostPerHour: financialNumberField,
  caregiverWeekendShiftCostPerHour: financialNumberField,
  weekShiftPricePerHour: financialNumberField,
  weekendShiftPricePerHour: financialNumberField,
});

const validationSchema = currentUser?.value.companyChargesAndPaysPerHours ?
  perHourValidationSchema : normalValidationSchema;

const groupFilter = useGroupFilterStore();

const commonInitialValues = {
  patientId: props.patient?.id,
  caregiverId: props.caregiver?.id,
  withTaxBill: false,
  shiftType: undefined,
  startTime: undefined,
  endTime: undefined,
  startDate: undefined,
  endDate: undefined,
  recurrenceType: undefined,
  recurrenceInterval: 1,
  recurrenceDays: undefined,
};

const fixedRateInitialValues = {
  ...commonInitialValues,
  caregiverWeekShiftCost: 0,
  caregiverWeekendShiftCost: 0,
  weekShiftPrice: 0,
  weekendShiftPrice: 0,
};

const perHourRateInitialValues = {
  ...commonInitialValues,
  caregiverWeekShiftCostPerHour: 0,
  caregiverWeekendShiftCostPerHour: 0,
  weekShiftPricePerHour: 0,
  weekendShiftPricePerHour: 0,
};

const { handleSubmit, values, errors, defineField, setFieldValue } = useForm<ShiftRecurrentCreateForm>({
  validationSchema,
  initialValues: currentUser?.value.companyChargesAndPaysPerHours ? perHourRateInitialValues : fixedRateInitialValues,
});

const [patientId, patientIdAttrs] = defineField('patientId');
const [caregiverId, caregiverIdAttrs] = defineField('caregiverId');
const [shiftType, shiftTypeAttrs] = defineField('shiftType');
const [startTime] = defineField('startTime');
const [endTime] = defineField('endTime');
const [startDate, startDateAttrs] = defineField('startDate');
const [endDate, endDateAttrs] = defineField('endDate');
const [recurrenceType, recurrenceTypeAttrs] = defineField('recurrenceType');
const [recurrenceInterval, recurrenceIntervalAttrs] = defineField('recurrenceInterval');
const [recurrenceDays, recurrenceDaysAttrs] = defineField('recurrenceDays');
const [caregiverWeekShiftCost, caregiverWeekShiftCostAttrs] = defineField('caregiverWeekShiftCost');
const [caregiverWeekendShiftCost, caregiverWeekendShiftCostAttrs] = defineField('caregiverWeekendShiftCost');
const [weekShiftPrice, weekShiftPriceAttrs] = defineField('weekShiftPrice');
const [weekendShiftPrice, weekendShiftPriceAttrs] = defineField('weekendShiftPrice');
const [caregiverWeekShiftCostPerHour, caregiverWeekShiftCostPerHourAttrs] = defineField(
  'caregiverWeekShiftCostPerHour');
const [caregiverWeekendShiftCostPerHour, caregiverWeekendShiftCostPerHourAttrs] = defineField(
  'caregiverWeekendShiftCostPerHour');
const [weekShiftPricePerHour, weekShiftPricePerHourAttrs] = defineField('weekShiftPricePerHour');
const [weekendShiftPricePerHour, weekendShiftPricePerHourAttrs] = defineField('weekendShiftPricePerHour');
const [withTaxBill, withTaxBillAttrs] = defineField('withTaxBill');

const groupsEnabled = computed(() => !!currentUser?.value?.companyGroupsEnabled);
const caregiverPickerGroupIds = ref<number[]>([]);
const patientPickerGroupIds = ref<number[]>([]);

const { data: caregivers, isFetching: isFetchingCaregivers } = useQuery({
  queryKey: ['caregiversForFilters', caregiverPickerGroupIds],
  queryFn: () => caregiversApi.indexForFilters(caregiverPickerGroupIds.value),
  placeholderData: keepPreviousData,
});
const { data: patients, isFetching: isFetchingPatients } = useQuery({
  queryKey: ['patientsForFilters', patientPickerGroupIds],
  queryFn: () => patientsApi.indexForFilters(patientPickerGroupIds.value),
  placeholderData: keepPreviousData,
});

watch(
  [patientId, () => groupFilter.groupIds, patients, groupsEnabled],
  () => {
    caregiverPickerGroupIds.value = groupsEnabled.value ?
      resolveMemberGroupIds(patientId.value, patients.value, groupFilter.groupIds ?? []) :
      [];
  },
  { immediate: true },
);

watch(
  [caregiverId, () => groupFilter.groupIds, caregivers, groupsEnabled],
  () => {
    patientPickerGroupIds.value = groupsEnabled.value ?
      resolveMemberGroupIds(caregiverId.value, caregivers.value, groupFilter.groupIds ?? []) :
      [];
  },
  { immediate: true },
);

watch(caregivers, (newList, oldList) => {
  if (oldList === undefined) return;
  if (!newList || !caregiverId.value) return;
  if (!newList.some((c: CaregiverFilter) => c.id === caregiverId.value)) {
    setFieldValue('caregiverId', undefined as never);
  }
});

watch(patients, (newList, oldList) => {
  if (oldList === undefined) return;
  if (!newList || !patientId.value) return;
  if (!newList.some((p: PatientFilter) => p.id === patientId.value)) {
    setFieldValue('patientId', undefined as never);
  }
});

const currentCaregiver = computed<CaregiverFilter | undefined>(
  () => caregivers.value?.find((caregiver: CaregiverFilter) => caregiver.id === caregiverId.value),
);

const currentPatient = computed(
  () => patients.value?.find((patient: PatientFilter) => patient.id === patientId.value),
);

const currentPatientIdRef = computed(() => currentPatient.value?.id);
const currentCaregiverIdRef = computed(() => currentCaregiver.value?.id);

// With groups enabled a shift needs a group shared by patient and caregiver, so a
// member outside every group can be paired with nobody: the other picker offers
// nobody instead of the global filter's people, who would all fail on save.
const patientWithoutGroups = computed(() => groupsEnabled.value && memberWithoutGroups(currentPatient.value));
const caregiverWithoutGroups = computed(() => groupsEnabled.value && memberWithoutGroups(currentCaregiver.value));
const caregiverOptions = computed(() => (patientWithoutGroups.value ? [] : caregivers.value));
const patientOptions = computed(() => (caregiverWithoutGroups.value ? [] : patients.value));
const withoutGroupsMessage = computed(() => {
  if (patientWithoutGroups.value) return t('shiftRecurrentCreateForm.patientWithoutGroups');
  if (caregiverWithoutGroups.value) return t('shiftRecurrentCreateForm.caregiverWithoutGroups');

  return undefined;
});

watch(patientWithoutGroups, (blocked) => {
  if (blocked && caregiverId.value) setFieldValue('caregiverId', undefined as never);
});

watch(caregiverWithoutGroups, (blocked) => {
  if (blocked && patientId.value) setFieldValue('patientId', undefined as never);
});

const canReadFinancialInfo = computed(() => hasPermission.value('shifts', 'read_financial_information'));

const caregiverHasActiveSalary = computed(
  () => currentCaregiver.value?.contractedWithSalary ?? false,
);

const { pricingStructure: defaultPricing, isLoading: isPricingLoading } = usePricingStructure(
  currentPatientIdRef,
  currentCaregiverIdRef,
  canReadFinancialInfo,
);

function updateShiftValuesForWeekend() {
  const defaultPricingData = defaultPricing.value ?? {};
  caregiverWeekendShiftCost.value = defaultPricingData.weekendCost ?? 0;
  weekendShiftPrice.value = defaultPricingData.weekendPrice ?? 0;
  caregiverWeekendShiftCostPerHour.value = defaultPricingData.weekendCostPerHour ?? 0;
  weekendShiftPricePerHour.value = defaultPricingData.weekendPricePerHour ?? 0;
}

function updateShiftValuesForWeek() {
  const defaultPricingData = defaultPricing.value ?? {};
  caregiverWeekShiftCost.value = defaultPricingData.weekCost ?? 0;
  weekShiftPrice.value = defaultPricingData.weekPrice ?? 0;
  caregiverWeekShiftCostPerHour.value = defaultPricingData.weekCostPerHour ?? 0;
  weekShiftPricePerHour.value = defaultPricingData.weekPricePerHour ?? 0;
}

function updateShiftValues() {
  updateShiftValuesForWeekend();
  updateShiftValuesForWeek();
  withTaxBill.value = defaultPricing.value?.withTaxBill ?? false;
}

interface ComplianceCheck {
  caregiverDisplayName: string;
  complianceCheck: {
    firstRuleText: string;
    secondRuleText: string;
    thirdRuleText: string;
  };
}

const complianceChecks = ref<ComplianceCheck[]>([]);
const showComplianceModal = ref(false);
const conflictDetector = ref<InstanceType<typeof ShiftConflictDetector>>();
const redirectToShiftsTimeoutMs = 1500;
function handleSuccess() {
  emit('refetch');
  emit('success');

  setTimeout(() => {
    emit('close');
  }, redirectToShiftsTimeoutMs);
}

function extractShiftsCount(data: unknown): number {
  if (Array.isArray(data)) return data.length;
  const obj = data as { shifts?: unknown[] };
  return obj?.shifts?.length ?? 0;
}

const submitButtonDisabled = ref(false);

const {
  mutate, error: createError, isIdle, isError, isPending, isSuccess,
} = useMutation<unknown, AxiosError>(
  {
    mutationFn: () => shiftsApi.create({ ...values, recurrent: true }),
    // The form stays on screen after a failed create (see the v-form v-if) so the
    // user can act on the message, e.g. pick another caregiver, and submit again.
    onError: () => {
      submitButtonDisabled.value = false;
    },
    onSuccess: (data) => {
      trackShiftEvent(AnalyticsEvents.shifts.createdRecurring, {
        patientId: patientId.value,
        caregiverId: caregiverId.value,
        recurrenceType: recurrenceType.value,
        shiftsCount: extractShiftsCount(data),
      });

      const responseData = data as { meta?: { complianceChecks?: ComplianceCheck[] } };
      if (responseData?.meta?.complianceChecks && responseData.meta.complianceChecks.length > 0) {
        complianceChecks.value = responseData.meta.complianceChecks;
        showComplianceModal.value = true;
      } else {
        handleSuccess();
      }
    },
  },
);

// Validation messages of the 400 (e.g. the caregiver shares no group with the
// patient); any other failure gets the generic message.
const createErrorMessage = computed(
  () => requestErrorMessage(createError.value, t('userSession.defaultError')),
);

function handleConflictCancel() {
  submitButtonDisabled.value = false;
}

function closeComplianceModal() {
  showComplianceModal.value = false;
  handleSuccess();
}

const onSubmit = handleSubmit(() => {
  submitButtonDisabled.value = true;
  conflictDetector.value?.checkConflicts({ ...values, recurrent: true });
});

watch(shiftType, () => {
  if (shiftType.value === 'day') {
    startTime.value = '08:00';
    endTime.value = '20:00';
  } else if (shiftType.value === 'night') {
    startTime.value = '20:00';
    endTime.value = '08:00';
  } else if (shiftType.value === '24') {
    endTime.value = startTime.value;
  } else if (shiftType.value === 'custom' && startTime.value) {
    // A custom shift opens as a one-hour range from the start it already has.
    endTime.value = addMinutesToTime(startTime.value, MINUTES_PER_HOUR);
  }
});
// In a custom shift the end follows the start (see endTimeFollowingStart).
watch(startTime, (newStart, previousStart) => {
  if (shiftType.value === '24') {
    endTime.value = startTime.value;
  } else if (shiftType.value === 'custom') {
    endTime.value = endTimeFollowingStart(previousStart, newStart, endTime.value);
  }
});

watch(recurrenceType, () => {
  if (recurrenceType.value === 'daily') {
    recurrenceDays.value = undefined;
  } else if (recurrenceType.value === 'weekly') {
    recurrenceInterval.value = 1;
  }
});

const days = [
  { title: 'Lunes', value: 'monday' }, { title: 'Martes', value: 'tuesday' },
  { title: 'Miércoles', value: 'wednesday' }, { title: 'Jueves', value: 'thursday' },
  { title: 'Viernes', value: 'friday' }, { title: 'Sábado', value: 'saturday' }, { title: 'Domingo', value: 'sunday' },
];

const recurrences = [
  { title: 'Diaria', value: 'daily' },
  { title: 'Semanal', value: 'weekly' },
];
const dailyRecurrenceOptions = [
  { title: 'Todos los días', value: 1 },
  { title: 'Día por medio', value: 2 },
  { title: 'Cada 3 días', value: 3 },
  { title: 'Cada 4 días', value: 4 },
  { title: 'Cada 5 días', value: 5 },
  { title: 'Cada 6 días', value: 6 },
];

const steps = hasPermission.value('shifts', 'read_financial_information') ?
  ['Información General', 'Precios', 'Recurrencia'] : ['Información General', 'Recurrencia'];

watch(isSuccess, async () => {
  if (isSuccess.value === true && !showComplianceModal.value) {
    await new Promise((resolve) => setTimeout(resolve, redirectToShiftsTimeoutMs));
    emit('close');
  }
});

function isFilled(value: boolean | string | number | null | undefined) {
  return (value !== '' && value !== null && value !== undefined);
}
function isTimeFilled(value: string | null | undefined) {
  return (value !== '' && value !== null && value !== undefined);
}
const currentStep = ref(0);
const isGeneralInformationStepCompleted = computed(() =>
  isFilled(patientId.value) &&
  isFilled(caregiverId.value) &&
  (isTimeFilled(startTime.value) && isTimeFilled(endTime.value)),
);
const isFinancialInformationPerHourStepCompleted = computed(() => {
  const requiredFields = [
    caregiverWeekShiftCostPerHour.value,
    caregiverWeekendShiftCostPerHour.value,
    weekShiftPricePerHour.value,
    weekendShiftPricePerHour.value,
    withTaxBill.value,
  ];

  return requiredFields.every(isFilled);
});
const isFinancialInformationStepCompleted = computed(() => {
  if (caregiverHasActiveSalary.value) {
    const priceFields = currentUser?.value.companyChargesAndPaysPerHours ?
      [weekShiftPricePerHour.value, weekendShiftPricePerHour.value] :
      [weekShiftPrice.value, weekendShiftPrice.value];

    return priceFields.every(isFilled);
  }
  if (currentUser?.value.companyChargesAndPaysPerHours) return isFinancialInformationPerHourStepCompleted.value;

  const requiredFields = [
    caregiverWeekShiftCost.value,
    caregiverWeekendShiftCost.value,
    weekShiftPrice.value,
    weekendShiftPrice.value,
    withTaxBill.value,
  ];

  return requiredFields.every(isFilled);
});
const isDailyRecurrenceCompleted = computed(() =>
  recurrenceType.value === 'daily' &&
  isFilled(recurrenceInterval.value),
);
const isWeeklyRecurrenceCompleted = computed(() =>
  recurrenceType.value === 'weekly' &&
  recurrenceDays.value?.length && recurrenceDays.value?.length > 0,
);
const isRecurrenceStepCompleted = computed(() =>
  isFilled(startDate.value) &&
  isFilled(endDate.value) &&
  isFilled(recurrenceType.value) &&
  (isDailyRecurrenceCompleted.value || isWeeklyRecurrenceCompleted.value),
);
// eslint-disable-next-line complexity
const canGoToNextStep = computed(() => {
  if (currentStep.value === 0 && !isGeneralInformationStepCompleted.value) return false;
  if (currentStep.value === 0 && isPricingLoading.value) return false;
  if (currentStep.value === 1 && !isFinancialInformationStepCompleted.value) return false;
  if (currentStep.value === steps.length - 1 && !isRecurrenceStepCompleted.value) return false;

  return true;
});

const nextYear = new Date().getFullYear() + 1;
const maxDate = `${nextYear}-12-31`;
watch(startDate, (newValue) => {
  if (newValue) {
    const selectedDate = new Date(newValue);
    const limitDate = new Date(maxDate);
    if (selectedDate > limitDate) {
      startDate.value = maxDate;
    }
  }
});
watch(endDate, (newValue) => {
  if (newValue) {
    const selectedDate = new Date(newValue);
    const limitDate = new Date(maxDate);
    if (selectedDate > limitDate) {
      endDate.value = maxDate;
    }
  }
});

watch(defaultPricing, () => {
  updateShiftValues();
}, { immediate: true });
</script>

<template>
  <v-card
    v-if="hasPermission('shifts', 'create')"
    class="w-100 pa-4"
    flat
  >
    <v-card-title class="mb-4 ml-2 align-baseline d-flex">
      {{ t('shiftRecurrentCreateForm.title') }}
      <v-spacer />
      <v-btn
        icon="mdi-window-close"
        variant="plain"
        @click="$emit('close')"
      />
    </v-card-title>
    <shifts-info-banner class="mx-2" />
    <v-form
      v-if="isIdle || isError"
      class="w-100 ma-0"
      @submit.prevent="onSubmit"
    >
      <v-stepper
        v-model="currentStep"
        flat
      >
        <template #default="{ prev, next }">
          <v-stepper-header>
            <template
              v-for="(step, index) in steps"
              :key="`${step}-step`"
            >
              <v-stepper-item
                :step="step"
                :value="index"
                icon="mdi-form-select"
                :title="steps[index]"
                :complete="index < currentStep"
                :color="index <= currentStep ? 'primary' : 'gray'"
              />

              <v-divider
                v-if="index !== steps.length - 1"
                :key="index"
              />
            </template>
          </v-stepper-header>
          <v-stepper-window>
            <v-stepper-window-item>
              <v-row class="mx-2 mt-4 d-flex">
                <v-autocomplete
                  v-model="patientId"
                  v-bind="patientIdAttrs"
                  class="px-2 v-col-6"
                  variant="outlined"
                  item-title="fullName"
                  item-value="id"
                  :label="t('shift.patient')"
                  :error-messages="errors.patientId"
                  :items="patientOptions"
                  :loading="isFetchingPatients"
                  :disabled="caregiverWithoutGroups"
                />
                <v-autocomplete
                  v-model="caregiverId"
                  v-bind="caregiverIdAttrs"
                  class="px-2 v-col-6"
                  variant="outlined"
                  item-title="fullName"
                  item-value="id"
                  :label="t('shift.caregiver')"
                  :error-messages="errors.caregiverId"
                  :items="caregiverOptions"
                  :loading="isFetchingCaregivers"
                  :disabled="patientWithoutGroups"
                />
              </v-row>
              <v-alert
                v-if="withoutGroupsMessage"
                class="mx-4 mb-4 member-without-groups-alert"
                type="warning"
                variant="tonal"
              >
                {{ withoutGroupsMessage }}
              </v-alert>
              <v-select
                v-model="shiftType"
                v-bind="shiftTypeAttrs"
                :label="t('shiftForm.shiftTypeTitle')"
                :error-messages="errors.shiftType"
                :items="[
                  { title: t('shiftForm.shiftTypeDay'), value: 'day' },
                  { title: t('shiftForm.shiftTypeNight'), value: 'night' },
                  { title: t('shiftForm.shiftType24'), value: '24' },
                  { title: t('shiftForm.shiftTypeCustom'), value: 'custom' }
                ]"
                variant="outlined"
                class="px-4 v-col-6"
              />
              <v-col
                v-if="shiftType === '24'"
                class="mb-4 d-flex flex-column"
              >
                <p class="ml-6 mb-2 text-caption text-grey">
                  {{ t('shiftForm.shiftType24Info') }}
                </p>
                <time-input
                  v-model="startTime"
                  class="w-50 pa-4"
                  label="Hora Inicio"
                  :error-messages="errors.startTime"
                />
              </v-col>
              <div
                v-if="shiftType === 'custom'"
                class="mb-4 d-flex"
              >
                <v-col>
                  <p class="ml-6 mb-2 text-caption text-grey">
                    {{ t('shiftForm.shiftTypeCustomStartInfo') }}
                  </p>
                  <time-input
                    v-model="startTime"
                    class="w-75 pa-4"
                    label="Hora Inicio"
                    :error-messages="errors.startTime"
                  />
                </v-col>
                <v-col>
                  <p class="ml-6 mb-2 text-caption text-grey">
                    {{ t('shiftForm.shiftTypeCustomEndInfo') }}
                  </p>
                  <time-input
                    v-model="endTime"
                    class="w-75 pa-4"
                    label="Hora Término"
                    :error-messages="errors.endTime"
                  />
                </v-col>
              </div>
            </v-stepper-window-item>
            <v-stepper-window-item
              v-if="hasPermission('shifts', 'read_financial_information')"
            >
              <template v-if="isPricingLoading && !caregiverHasActiveSalary">
                <v-card
                  :title="t('shiftBatchCreateForm.priceTitle')"
                  flat
                >
                  <v-row class="mx-2 mt-4 mb-2 d-flex">
                    <v-skeleton-loader
                      class="px-2 v-col-6"
                      type="text"
                    />
                    <v-skeleton-loader
                      class="px-2 v-col-6"
                      type="text"
                    />
                  </v-row>
                </v-card>
                <v-card
                  :title="t('shiftBatchCreateForm.caregiverCostTitle')"
                  flat
                >
                  <v-row class="mx-2 mt-4 mb-2 d-flex">
                    <v-skeleton-loader
                      class="px-2 v-col-6"
                      type="text"
                    />
                    <v-skeleton-loader
                      class="px-2 v-col-6"
                      type="text"
                    />
                  </v-row>
                </v-card>
                <v-skeleton-loader
                  class="mx-4"
                  type="text"
                />
              </template>
              <template v-else>
                <v-card
                  :title="t('shiftBatchCreateForm.priceTitle')"
                  flat
                >
                  <v-row
                    v-if="currentUser?.companyChargesAndPaysPerHours"
                    class="mx-2 mt-4 mb-2 d-flex"
                  >
                    <price-input
                      v-model="weekShiftPricePerHour"
                      v-bind="weekShiftPricePerHourAttrs"
                      class="px-2 v-col-6"
                      :label="t('shiftBatchCreateForm.weekShiftPricePerHour')"
                      :error-messages="errors.weekShiftPricePerHour"
                    />
                    <price-input
                      v-model="weekendShiftPricePerHour"
                      v-bind="weekendShiftPricePerHourAttrs"
                      class="px-2 v-col-6"
                      :label="t('shiftBatchCreateForm.weekendShiftPricePerHour')"
                      :error-messages="errors.weekendShiftPricePerHour"
                    />
                  </v-row>
                  <v-row
                    v-else
                    class="mx-2 mt-4 mb-2 d-flex"
                  >
                    <price-input
                      v-model="weekShiftPrice"
                      v-bind="weekShiftPriceAttrs"
                      class="px-2 v-col-6"
                      :label="t('shiftBatchCreateForm.weekShiftPrice')"
                      :error-messages="errors.weekShiftPrice"
                    />
                    <price-input
                      v-model="weekendShiftPrice"
                      v-bind="weekendShiftPriceAttrs"
                      class="px-2 v-col-6"
                      :label="t('shiftBatchCreateForm.weekendShiftPrice')"
                      :error-messages="errors.weekendShiftPrice"
                    />
                  </v-row>
                </v-card>
                <v-alert
                  v-if="caregiverHasActiveSalary"
                  type="info"
                  variant="tonal"
                  class="mx-4 mb-4"
                >
                  {{ t('shiftForm.caregiverHasSalaryInfo') }}
                </v-alert>
                <template v-else>
                  <v-card
                    :title="t('shiftBatchCreateForm.caregiverCostTitle')"
                    flat
                  >
                    <v-row
                      v-if="currentUser?.companyChargesAndPaysPerHours"
                      class="mx-2 mt-4 mb-2 d-flex"
                    >
                      <price-input
                        v-model="caregiverWeekShiftCostPerHour"
                        v-bind="caregiverWeekShiftCostPerHourAttrs"
                        class="px-2 v-col-6"
                        :label="t('shiftBatchCreateForm.caregiverWeekShiftCostPerHour')"
                        :error-messages="errors.caregiverWeekShiftCostPerHour"
                      />
                      <price-input
                        v-model="caregiverWeekendShiftCostPerHour"
                        v-bind="caregiverWeekendShiftCostPerHourAttrs"
                        class="px-2 v-col-6"
                        :label="t('shiftBatchCreateForm.caregiverWeekendShiftCostPerHour')"
                        :error-messages="errors.caregiverWeekendShiftCostPerHour"
                      />
                    </v-row>
                    <v-row
                      v-else
                      class="mx-2 mt-4 mb-2 d-flex"
                    >
                      <price-input
                        v-model="caregiverWeekShiftCost"
                        v-bind="caregiverWeekShiftCostAttrs"
                        class="px-2 v-col-6"
                        :label="t('shiftBatchCreateForm.caregiverWeekShiftCost')"
                        :error-messages="errors.caregiverWeekShiftCost"
                      />
                      <price-input
                        v-model="caregiverWeekendShiftCost"
                        v-bind="caregiverWeekendShiftCostAttrs"
                        class="px-2 v-col-6"
                        :label="t('shiftBatchCreateForm.caregiverWeekendShiftCost')"
                        :error-messages="errors.caregiverWeekendShiftCost"
                      />
                    </v-row>
                  </v-card>
                  <v-radio-group
                    v-model="withTaxBill"
                    v-bind="withTaxBillAttrs"
                    :label="t('shiftBatchCreateForm.withTaxBillTitle')"
                    :error-messages="errors.withTaxBill"
                    inline
                  >
                    <v-radio
                      :label="t('shiftBatchCreateForm.yes')"
                      :value="true"
                    />
                    <v-radio
                      :label="t('shiftBatchCreateForm.no')"
                      :value="false"
                    />
                  </v-radio-group>
                </template>
              </template>
            </v-stepper-window-item>
            <v-stepper-window-item>
              <v-row class="mx-2 mt-4 d-flex">
                <v-text-field
                  v-model="startDate"
                  v-bind="startDateAttrs"
                  class="px-2 v-col-6"
                  type="date"
                  label="Fecha desde"
                  variant="outlined"
                  :error-messages="errors.startDate"
                  :max="maxDate"
                />
                <v-text-field
                  v-model="endDate"
                  v-bind="endDateAttrs"
                  class="px-2 v-col-6"
                  type="date"
                  label="Fecha hasta"
                  variant="outlined"
                  :error-messages="errors.endDate"
                  :max="maxDate"
                />
              </v-row>
              <v-row class="mx-2 mt-4 d-flex">
                <v-select
                  v-model="recurrenceType"
                  v-bind="recurrenceTypeAttrs"
                  class="px-2 v-col-6"
                  variant="outlined"
                  :items="recurrences"
                  :label="t('shiftRecurrentCreateForm.recurrenceType')"
                  :error-messages="errors.recurrenceType"
                />
                <v-select
                  v-if="recurrenceType === 'daily'"
                  v-model="recurrenceInterval"
                  v-bind="recurrenceIntervalAttrs"
                  class="px-2 v-col-6"
                  variant="outlined"
                  :items="dailyRecurrenceOptions"
                  :label="t('shiftRecurrentCreateForm.recurrenceInterval')"
                  :error-messages="errors.recurrenceInterval"
                />
                <v-autocomplete
                  v-if="recurrenceType === 'weekly'"
                  v-model="recurrenceDays"
                  v-bind="recurrenceDaysAttrs"
                  :items="days"
                  :label="t('shiftRecurrentCreateForm.recurrenceDays')"
                  :error-messages="errors.recurrenceDays"
                  class="px-2 v-col-6"
                  variant="outlined"
                  :menu-props="{ maxHeight: '400' }"
                  chips
                  multiple
                />
              </v-row>
            </v-stepper-window-item>
          </v-stepper-window>
          <v-stepper-actions
            :class="currentStep === 0 ? 'justify-end' : 'justify-between'"
            :disabled="canGoToNextStep ? false : 'next'"
          >
            <template #prev>
              <v-btn
                v-if="currentStep !== 0"
                @click="prev"
              >
                {{ t('shiftRecurrentCreateForm.previous') }}
              </v-btn>
            </template>
            <template #next>
              <v-btn
                v-if="currentStep !== steps.length - 1"
                class="align-self-end"
                color="primary"
                @click="next"
              >
                {{ t('shiftRecurrentCreateForm.next') }}
              </v-btn>
              <v-btn
                v-else
                variant="flat"
                color="primary"
                type="submit"
                :disabled="submitButtonDisabled || isPricingLoading"
              >
                {{ t('shiftBatchCreateForm.submit') }}
              </v-btn>
            </template>
          </v-stepper-actions>
        </template>
      </v-stepper>
    </v-form>
    <v-col
      v-if="isPending"
      class="pb-20"
    >
      <v-row class="justify-center d-flex">
        <v-progress-circular
          class="mx-auto"
          indeterminate
          color="primary"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Estamos creando los turnos, esto puede tomar unos segundos...</p>
      </v-row>
    </v-col>
    <v-col v-if="isSuccess">
      <v-row class="justify-center d-flex">
        <v-icon
          icon="mdi-check-circle"
          color="primary"
          size="50"
        />
      </v-row>
      <v-row class="justify-center mt-8 d-flex">
        <p>Turnos creados exitosamente!</p>
      </v-row>
    </v-col>
    <v-alert
      v-if="isError"
      class="mt-4 rounded create-error-alert"
      type="error"
    >
      {{ createErrorMessage }}
    </v-alert>
    <compliance-check-modal
      v-model="showComplianceModal"
      :compliance-checks="complianceChecks"
      @update:model-value="closeComplianceModal"
    />

    <shift-conflict-detector
      ref="conflictDetector"
      @confirm="mutate"
      @cancel="handleConflictCancel"
    />
  </v-card>
</template>
